import { useCallback, useEffect, useRef, useState } from "react"
import { isNil, omitBy, pick } from "lodash"
import type { UnknownAction } from "@reduxjs/toolkit"
import type { ReduxState } from "model/state"
import { usePreferences } from "hooks/use-preferences"
import { useSaveContextPreferences } from "hooks/preferences"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { usePanelId } from "../context/panel-id"
import { useIsRealInstance } from "./use-is-real-instance"
import type { PanelStateBase } from "../store/create-panel-slice"

// A feature's per-feature divergences; an absent key uses the shared default.
export interface SyncResolveResult {
  // Override the fallback subtype. Present-but-undefined disables it (asset-view's 3-way gating).
  fallbackSubType?: string | undefined
  // Module-config-seeded fields the legacy fallback must not overwrite.
  omitFallbackKeys?: string[]
  // Merged into the no-saved-prefs backfill (asset-view initialView). Memoize in the wrapper.
  backfillSeed?: Record<string, unknown>
}

export type SyncResolve = (context: {
  panelId: string | undefined
  wasInitializedAtMount: boolean
}) => SyncResolveResult

export interface CreateSyncPreferencesHookConfig<S extends PanelStateBase> {
  // Reads this slice's per-panel slot (undefined when absent).
  selectPanelState: (state: ReduxState, dashboardId: string, panelKey: string) => S | undefined
  defaultState: S
  // Un-suffixed subtype + panel-id fallback when no PanelIdProvider exists.
  preferenceBase: string
  // Single-sourced from the slice; also drives its setPreferences whitelist.
  preferenceFields: readonly string[]
  // Adapters to the slice actions (the wrapper spreads prefs into the typed payload).
  setPreferences: (dashboardId: string, panelId: string, prefs: Record<string, unknown>) => UnknownAction
  clearPendingClonePersist: (dashboardId: string, panelId: string) => UnknownAction
}

// Builds a per-panel preferences-sync hook owning the machinery shared by every panel sync.
// A feature supplies its divergences via the optional `resolve` callback (gets the mount latches).
export function createSyncPreferencesHook<S extends PanelStateBase>(
  config: CreateSyncPreferencesHookConfig<S>,
) {
  const {
    selectPanelState,
    defaultState,
    preferenceBase,
    preferenceFields,
    setPreferences,
    clearPendingClonePersist,
  } = config

  return function useSyncPanelPreferences(resolve?: SyncResolve): boolean {
    const dashboardId = useDashboardId()
    const panelId = usePanelId()
    const dispatch = useAppDispatch()
    // Only suppress this panel's write when the workspace writer actually owns persistence here —
    // a flag-on account whose dashboard hasn't (or failed to) migrate to a real UUID instance has
    // no other writer, so it must keep using this legacy path (see use-is-real-instance.ts).
    const skipSave = useIsRealInstance(dashboardId)
    const panelKey = panelId ?? preferenceBase

    const state = useAppSelector(
      (s) => selectPanelState(s, dashboardId, panelKey) ?? defaultState,
    )

    // Slot already populated at mount (clone or remount) → don't re-load via the legacy fallback.
    const [wasInitializedAtMount] = useState(() => state.isPreferencesLoaded)
    // True only for a real clone, gating the one-time clone-persist save below.
    const [wasClonedAtMount] = useState(() => state.pendingClonePersist === true)

    const resolved = resolve?.({ panelId, wasInitializedAtMount }) ?? {}
    const fallbackSubType = "fallbackSubType" in resolved
      ? resolved.fallbackSubType
      : panelId && !wasInitializedAtMount
        ? preferenceBase
        : undefined
    const { omitFallbackKeys, backfillSeed } = resolved

    const setPreferencesAction = useCallback(
      (prefs: Record<string, unknown>) => setPreferences(dashboardId, panelKey, prefs),
      [dashboardId, panelKey],
    )

    const ownSubType = panelId ? `${preferenceBase}.${panelId}` : preferenceBase

    const isLoaded = usePreferences(
      ownSubType,
      [...preferenceFields],
      state,
      dispatch,
      setPreferencesAction,
      {
        fallbackSubType,
        ...(omitFallbackKeys && omitFallbackKeys.length > 0 && { omitFallbackKeys }),
        // Returning to a dashboard (key-remount) re-runs the one-shot load; when this slot was
        // already populated at mount, the slice holds the live value — possibly an edit the
        // debounced save hadn't flushed — so skip the load rather than clobber it with the
        // older cached read.
        skipInitialLoad: wasInitializedAtMount,
        skipSave,
      },
    )

    // No saved prefs: usePreferences flips isLoaded without dispatching, so the slice flag would
    // never flip. Dispatch to flip it, merging any backfill seed (asset-view's initialView).
    useEffect(() => {
      if (isLoaded && !state.isPreferencesLoaded) {
        dispatch(setPreferences(dashboardId, panelKey, backfillSeed ?? {}))
      }
    }, [isLoaded, state.isPreferencesLoaded, dashboardId, panelKey, backfillSeed, dispatch])

    // A clone's state lives only in Redux until persisted once; the ref latch stops a later remount
    // from re-persisting after the marker is consumed.
    const saveOwnPreferences = useSaveContextPreferences(ownSubType) as unknown as (
      value: Record<string, unknown>,
    ) => void
    const hasPersistedCloneRef = useRef(false)
    useEffect(() => {
      if (!wasClonedAtMount || !isLoaded || !panelId || hasPersistedCloneRef.current) return
      hasPersistedCloneRef.current = true
      // Instance mode: the workspace writer persists the clone, so suppress the per-panel clone
      // write while still consuming the pendingClonePersist marker.
      if (!skipSave) {
        const prefs = omitBy(pick(state, [...preferenceFields]), isNil) as Record<string, unknown>
        if (Object.keys(prefs).length > 0) {
          saveOwnPreferences(prefs)
        }
      }
      dispatch(clearPendingClonePersist(dashboardId, panelKey))
    }, [wasClonedAtMount, isLoaded, panelId, skipSave, state, saveOwnPreferences, dispatch, dashboardId, panelKey])

    // The SLICE flag, not usePreferences' — the latter flips a render before the slot is populated.
    return state.isPreferencesLoaded
  }
}
